import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { useGym } from '../../contexts/GymContext';
import Button from '../common/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatPhoneNumber, handlePhoneInput } from '../../utils/phoneFormat';

const Settings = () => {
  const { user, updateUser } = useContext(AuthContext);
  const { userGyms, addGym, removeGym, setPrimaryGym, refreshGyms } = useGym();
  const [activeTab, setActiveTab] = useState('account');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Profile settings state
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    hasPassword: false
  });

  // Password settings state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Notification preferences state
  const [notificationData, setNotificationData] = useState({
    // Member notifications
    emailReservationConfirmation: true,
    emailReservationReminder: true,
    emailBoardUpdates: false,
    smsReservationReminder: false,
    reminderHoursBefore: 2,
    // Manager notifications
    emailManagerProductUpdates: false,
    emailManagerOnReservationMade: false,
    emailManagerOnReservationCanceled: false,
    emailManagerDailySummary: false,
    emailManagerOnCapacityReached: false
  });

  // Gym settings state
  const [gymData, setGymData] = useState({
    gymId: ''
  });
  const [gyms, setGyms] = useState([]);
  const [gymSearchQuery, setGymSearchQuery] = useState('');
  const [filteredGyms, setFilteredGyms] = useState([]);
  const [showGymDropdown, setShowGymDropdown] = useState(false);
  const [selectedGymName, setSelectedGymName] = useState('');

  // Booking usage state
  const [bookingUsage, setBookingUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    loadUserSettings();
    loadGyms();
    loadBookingUsage();
  }, []);

  const loadUserSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to load settings');

      const data = await response.json();

      setProfileData({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phone: data.phone ? formatPhoneNumber(data.phone) : '',
        hasPassword: data.hasPassword || false
      });

      setNotificationData({
        // Member notifications
        emailReservationConfirmation: data.emailReservationConfirmation ?? true,
        emailReservationReminder: data.emailReservationReminder ?? true,
        emailBoardUpdates: data.emailBoardUpdates ?? false,
        smsReservationReminder: data.smsReservationReminder ?? false,
        reminderHoursBefore: data.reminderHoursBefore || 2,
        // Manager notifications
        emailManagerProductUpdates: data.emailManagerProductUpdates ?? false,
        emailManagerOnReservationMade: data.emailManagerOnReservationMade ?? false,
        emailManagerOnReservationCanceled: data.emailManagerOnReservationCanceled ?? false,
        emailManagerDailySummary: data.emailManagerDailySummary ?? false,
        emailManagerOnCapacityReached: data.emailManagerOnCapacityReached ?? false
      });

      setGymData({
        gymId: data.gymId || ''
      });

      // Set the current gym name for display
      if (data.gymId && gyms.length > 0) {
        const currentGym = gyms.find(g => g.id === data.gymId);
        if (currentGym) {
          setSelectedGymName(`${currentGym.name} - ${currentGym.city}, ${currentGym.state}`);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    }
  };

  const loadBookingUsage = async () => {
    try {
      setLoadingUsage(true);
      const response = await fetch(`${API_URL}/api/platform-settings/booking-usage/me`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to load booking usage');

      const data = await response.json();
      setBookingUsage(data);
    } catch (error) {
      console.error('Error loading booking usage:', error);
      // Don't show error - just means feature isn't enabled
      setBookingUsage(null);
    } finally {
      setLoadingUsage(false);
    }
  };

  const loadGyms = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gyms`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to load gyms');
      const data = await response.json();
      setGyms(data);
      setFilteredGyms(data);
    } catch (error) {
      console.error('Error loading gyms:', error);
    }
  };

  // Update selected gym name when gyms are loaded
  useEffect(() => {
    if (gymData.gymId && gyms.length > 0) {
      const currentGym = gyms.find(g => g.id === parseInt(gymData.gymId));
      if (currentGym) {
        setSelectedGymName(`${currentGym.name} - ${currentGym.city}, ${currentGym.state}`);
        setGymSearchQuery(`${currentGym.name} - ${currentGym.city}, ${currentGym.state}`);
      }
    }
  }, [gyms, gymData.gymId]);

  // Filter gyms based on search query
  const handleGymSearch = (query) => {
    setGymSearchQuery(query);
    setShowGymDropdown(true);

    if (!query.trim()) {
      setFilteredGyms(gyms);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const filtered = gyms.filter(gym =>
      gym.name.toLowerCase().includes(lowerQuery) ||
      gym.city.toLowerCase().includes(lowerQuery) ||
      gym.state.toLowerCase().includes(lowerQuery) ||
      `${gym.city}, ${gym.state}`.toLowerCase().includes(lowerQuery)
    );
    setFilteredGyms(filtered);
  };

  const handleGymSelect = (gym) => {
    setGymData({ gymId: gym.id });
    setSelectedGymName(`${gym.name} - ${gym.city}, ${gym.state}`);
    setGymSearchQuery(`${gym.name} - ${gym.city}, ${gym.state}`);
    setShowGymDropdown(false);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Remove formatting from phone number before sending
      const phoneDigits = profileData.phone.replace(/\D/g, '');

      const response = await fetch(`${API_URL}/api/users/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...profileData,
          phone: phoneDigits || null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update profile');
      }

      const data = await response.json();
      setMessage({ type: 'success', text: 'Profile updated successfully' });

      // Update user context if needed
      if (updateUser) {
        updateUser(data.user);
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      setLoading(false);
      return;
    }

    try {
      // Use the new set-password endpoint that works for both OAuth and regular users
      const response = await fetch(`${API_URL}/api/auth/set-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword || undefined,
          password: passwordData.newPassword
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update password');
      }

      const data = await response.json();
      setMessage({ type: 'success', text: data.message || 'Password updated successfully' });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Update hasPassword state if this was the first time setting a password
      if (!profileData.hasPassword) {
        setProfileData({ ...profileData, hasPassword: true });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_URL}/api/users/notifications`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(notificationData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update notification preferences');
      }

      setMessage({ type: 'success', text: 'Notification preferences updated successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleGymUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_URL}/api/users/gym`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ gymId: parseInt(gymData.gymId) })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update gym');
      }

      const data = await response.json();

      // Update user context to refresh the authentication with new gym
      if (updateUser) {
        await updateUser();
      }

      setMessage({ type: 'success', text: 'Home gym updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if a tab should be shown for the current user
  const isManagerRole = user?.role && ['gym_manager', 'admin', 'platform_admin', 'chain_owner'].includes(user.role);

  const tabs = [
    {
      id: 'account',
      name: 'Account',
      icon: (
        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      color: 'teal',
      showForAll: true
    },
    {
      id: 'notifications',
      name: 'Notifications',
      icon: (
        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
      color: 'magenta',
      showForAll: true
    },
    {
      id: 'gyms',
      name: 'My Gyms',
      icon: (
        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      color: 'coral',
      showForMembers: true
    },
    {
      id: 'usage',
      name: 'Booking Usage',
      icon: (
        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      color: 'purple',
      showForMembers: true,
      hide: !bookingUsage || !bookingUsage.enabled
    }
  ].filter(tab => {
    if (tab.hide) return false;
    if (tab.showForAll) return true;
    if (tab.showForMembers && !isManagerRole) return true;
    return false;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 py-6">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <span className="w-1 h-8 bg-gradient-to-b from-teal-500 to-purple-500 rounded"></span>
            Settings
          </h1>
          <p className="mt-1 text-sm font-medium text-slate-600">
            Manage your account settings and preferences
          </p>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg border-2 font-medium ${
            message.type === 'success'
              ? 'bg-teal-100 text-teal-800 border-teal-500'
              : 'bg-coral-100 text-coral-800 border-coral-500'
          }`}>
            <p className="text-sm font-bold">{message.text}</p>
          </div>
        )}

        <div className="bg-white rounded-xl border-2 border-slate-900 shadow-retro">
          {/* Tabs */}
          <div className="border-b-2 border-slate-900">
            <nav className="flex -mb-px gap-1 p-1 overflow-x-auto">
              {tabs.map((tab) => {
                const getIconColor = () => {
                  if (activeTab === tab.id) {
                    return `text-${tab.color}-600`;
                  }
                  return `text-${tab.color}-500`;
                };

                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setMessage({ type: '', text: '' });
                    }}
                    className={`px-3 sm:px-6 py-3 text-sm font-bold rounded-lg transition-all touch-manipulation flex items-center gap-2 whitespace-nowrap flex-shrink-0 ${
                      activeTab === tab.id
                        ? 'text-teal-600 bg-teal-50 border-2 border-teal-500'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 border-2 border-transparent'
                    }`}
                  >
                    <span className={getIconColor()}>
                      {tab.icon}
                    </span>
                    <span className="hidden sm:inline">{tab.name}</span>
                    <span className="sm:hidden">{tab.name.replace('My ', '')}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Account Tab - Combined Profile & Password */}
            {activeTab === 'account' && (
              <div className="space-y-8">
                {/* Profile Section */}
                <form onSubmit={handleProfileUpdate} className="space-y-6">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <span className="w-1 h-5 bg-teal-500 rounded"></span>
                      Profile Information
                    </h3>

                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-bold text-slate-700 mb-2">
                          First Name
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          value={profileData.firstName}
                          onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                          className="block w-full rounded-lg border-2 border-slate-900 px-3 py-2 text-slate-900 font-medium focus:border-teal-500 focus:ring-2 focus:ring-teal-500 transition-all"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="lastName" className="block text-sm font-bold text-slate-700 mb-2">
                          Last Name
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          value={profileData.lastName}
                          onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                          className="block w-full rounded-lg border-2 border-slate-900 px-3 py-2 text-slate-900 font-medium focus:border-teal-500 focus:ring-2 focus:ring-teal-500 transition-all"
                          required
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          value={profileData.email}
                          onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                          className="block w-full rounded-lg border-2 border-slate-900 px-3 py-2 text-slate-900 font-medium focus:border-teal-500 focus:ring-2 focus:ring-teal-500 transition-all"
                          required
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="phone" className="block text-sm font-bold text-slate-700 mb-2">
                          Phone Number
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="tel"
                            id="phone"
                            value={profileData.phone}
                            onChange={(e) => setProfileData({ ...profileData, phone: handlePhoneInput(e.target.value) })}
                            placeholder="(555) 123-4567"
                            maxLength={14}
                            className="block w-full rounded-lg border-2 border-slate-900 px-3 py-2 text-slate-900 font-medium focus:border-teal-500 focus:ring-2 focus:ring-teal-500 transition-all"
                          />
                          {profileData.phone && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={async () => {
                                if (window.confirm('Are you sure you want to remove your phone number? You will no longer receive text reminders.')) {
                                  setLoading(true);
                                  setMessage({ type: '', text: '' });
                                  try {
                                    const response = await fetch(`${API_URL}/api/users/profile`, {
                                      method: 'PUT',
                                      headers: {
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                        'Content-Type': 'application/json'
                                      },
                                      body: JSON.stringify({
                                        ...profileData,
                                        phone: null
                                      })
                                    });

                                    if (!response.ok) {
                                      const error = await response.json();
                                      throw new Error(error.error || 'Failed to remove phone number');
                                    }

                                    setProfileData({ ...profileData, phone: '' });
                                    setMessage({ type: 'success', text: 'Phone number removed successfully' });

                                    // Update user context
                                    if (updateUser) {
                                      await updateUser();
                                    }
                                  } catch (error) {
                                    setMessage({ type: 'error', text: error.message });
                                  } finally {
                                    setLoading(false);
                                  }
                                }
                              }}
                              disabled={loading}
                            >
                              Clear
                            </Button>
                          )}
                        </div>
                        <p className="mt-2 text-sm font-medium text-slate-600">
                          Used for reservation confirmations and reminders
                        </p>
                      </div>
                    </div>

                    {user?.oauth_provider && (
                      <p className="mt-4 text-sm font-medium text-slate-600">
                        Signed in with {user.oauth_provider.charAt(0).toUpperCase() + user.oauth_provider.slice(1)}
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" disabled={loading}>
                      {loading ? <LoadingSpinner size="small" /> : 'Save Profile'}
                    </Button>
                  </div>
                </form>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t-2 border-slate-900"></div>
                  </div>
                </div>

                {/* Password Section - Show for all users */}
                <form onSubmit={handlePasswordUpdate} className="space-y-6">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <span className="w-1 h-5 bg-purple-500 rounded"></span>
                      {profileData.hasPassword ? 'Change Password' : 'Set Password'}
                    </h3>

                    {/* Info message for OAuth users without password */}
                    {user?.oauth_provider && !profileData.hasPassword && (
                      <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-500 rounded-lg">
                        <div className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <h4 className="text-sm font-bold text-blue-900 mb-1">
                              Add a Password to Your Account
                            </h4>
                            <p className="text-sm font-medium text-blue-800">
                              You signed up with {user.oauth_provider.charAt(0).toUpperCase() + user.oauth_provider.slice(1)}.
                              Setting a password will allow you to log in with either method.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-4">
                      {/* Only show current password field if user already has a password */}
                      {profileData.hasPassword && (
                        <div>
                          <label htmlFor="currentPassword" className="block text-sm font-bold text-slate-700 mb-2">
                            Current Password
                          </label>
                          <input
                            type="password"
                            id="currentPassword"
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                            className="block w-full rounded-lg border-2 border-slate-900 px-3 py-2 text-slate-900 font-medium focus:border-teal-500 focus:ring-2 focus:ring-teal-500 transition-all"
                            required
                          />
                        </div>
                      )}

                      <div>
                        <label htmlFor="newPassword" className="block text-sm font-bold text-slate-700 mb-2">
                          {profileData.hasPassword ? 'New Password' : 'Password'}
                        </label>
                        <input
                          type="password"
                          id="newPassword"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                          className="block w-full rounded-lg border-2 border-slate-900 px-3 py-2 text-slate-900 font-medium focus:border-teal-500 focus:ring-2 focus:ring-teal-500 transition-all"
                          required
                          minLength={6}
                        />
                        <p className="mt-2 text-sm font-medium text-slate-600">Must be at least 6 characters</p>
                      </div>

                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-bold text-slate-700 mb-2">
                          Confirm {profileData.hasPassword ? 'New ' : ''}Password
                        </label>
                        <input
                          type="password"
                          id="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                          className="block w-full rounded-lg border-2 border-slate-900 px-3 py-2 text-slate-900 font-medium focus:border-teal-500 focus:ring-2 focus:ring-teal-500 transition-all"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" disabled={loading}>
                      {loading ? <LoadingSpinner size="small" /> : (profileData.hasPassword ? 'Update Password' : 'Set Password')}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <form onSubmit={handleNotificationUpdate} className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <span className="w-1 h-5 bg-teal-500 rounded"></span>
                    Notification Preferences
                  </h3>

                  <div className="space-y-4">
                    {/* Manager Notifications */}
                    {isManagerRole ? (
                      <>
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="emailManagerProductUpdates"
                              type="checkbox"
                              checked={notificationData.emailManagerProductUpdates}
                              onChange={(e) => setNotificationData({ ...notificationData, emailManagerProductUpdates: e.target.checked })}
                              className="h-5 w-5 text-teal-600 focus:ring-teal-500 border-2 border-slate-900 rounded transition-all"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="emailManagerProductUpdates" className="font-bold text-slate-700">
                              Product and Feature Updates
                            </label>
                            <p className="font-medium text-slate-600">Stay informed about new features and product announcements</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="emailManagerOnReservationMade"
                              type="checkbox"
                              checked={notificationData.emailManagerOnReservationMade}
                              onChange={(e) => setNotificationData({ ...notificationData, emailManagerOnReservationMade: e.target.checked })}
                              className="h-5 w-5 text-teal-600 focus:ring-teal-500 border-2 border-slate-900 rounded transition-all"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="emailManagerOnReservationMade" className="font-bold text-slate-700">
                              New Reservation Alerts
                            </label>
                            <p className="font-medium text-slate-600">Get notified when a member makes a reservation at your gym</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="emailManagerOnReservationCanceled"
                              type="checkbox"
                              checked={notificationData.emailManagerOnReservationCanceled}
                              onChange={(e) => setNotificationData({ ...notificationData, emailManagerOnReservationCanceled: e.target.checked })}
                              className="h-5 w-5 text-teal-600 focus:ring-teal-500 border-2 border-slate-900 rounded transition-all"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="emailManagerOnReservationCanceled" className="font-bold text-slate-700">
                              Cancellation Alerts
                            </label>
                            <p className="font-medium text-slate-600">Get notified when a member cancels their reservation</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="emailManagerDailySummary"
                              type="checkbox"
                              checked={notificationData.emailManagerDailySummary}
                              onChange={(e) => setNotificationData({ ...notificationData, emailManagerDailySummary: e.target.checked })}
                              className="h-5 w-5 text-teal-600 focus:ring-teal-500 border-2 border-slate-900 rounded transition-all"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="emailManagerDailySummary" className="font-bold text-slate-700">
                              Daily Reservation Summary
                            </label>
                            <p className="font-medium text-slate-600">Receive a daily email with reservation statistics and upcoming bookings</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="emailManagerOnCapacityReached"
                              type="checkbox"
                              checked={notificationData.emailManagerOnCapacityReached}
                              onChange={(e) => setNotificationData({ ...notificationData, emailManagerOnCapacityReached: e.target.checked })}
                              className="h-5 w-5 text-teal-600 focus:ring-teal-500 border-2 border-slate-900 rounded transition-all"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="emailManagerOnCapacityReached" className="font-bold text-slate-700">
                              Capacity Alerts
                            </label>
                            <p className="font-medium text-slate-600">Get notified when a time slot reaches full capacity</p>
                          </div>
                        </div>
                      </>
                    ) : (
                      /* Member Notifications */
                      <>
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="emailReservationConfirmation"
                              type="checkbox"
                              checked={notificationData.emailReservationConfirmation}
                              onChange={(e) => setNotificationData({ ...notificationData, emailReservationConfirmation: e.target.checked })}
                              className="h-5 w-5 text-teal-600 focus:ring-teal-500 border-2 border-slate-900 rounded transition-all"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="emailReservationConfirmation" className="font-bold text-slate-700">
                              Reservation Confirmations
                            </label>
                            <p className="font-medium text-slate-600">Receive email when you make a reservation</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="emailReservationReminder"
                              type="checkbox"
                              checked={notificationData.emailReservationReminder}
                              onChange={(e) => setNotificationData({ ...notificationData, emailReservationReminder: e.target.checked })}
                              className="h-5 w-5 text-teal-600 focus:ring-teal-500 border-2 border-slate-900 rounded transition-all"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="emailReservationReminder" className="font-bold text-slate-700">
                              Reservation Reminders
                            </label>
                            <p className="font-medium text-slate-600">Receive reminder before your reservation</p>
                          </div>
                        </div>

                        {notificationData.emailReservationReminder && (
                          <div className="ml-7">
                            <label htmlFor="reminderHoursBefore" className="block text-sm font-bold text-slate-700 mb-2">
                              Send reminder
                            </label>
                            <select
                              id="reminderHoursBefore"
                              value={notificationData.reminderHoursBefore}
                              onChange={(e) => setNotificationData({ ...notificationData, reminderHoursBefore: parseInt(e.target.value) })}
                              className="block w-48 rounded-lg border-2 border-slate-900 px-3 py-2 text-slate-900 font-medium focus:border-teal-500 focus:ring-2 focus:ring-teal-500 transition-all"
                            >
                              <option value={1}>1 hour before</option>
                              <option value={2}>2 hours before</option>
                              <option value={4}>4 hours before</option>
                              <option value={24}>1 day before</option>
                            </select>
                          </div>
                        )}

                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="emailBoardUpdates"
                              type="checkbox"
                              checked={notificationData.emailBoardUpdates}
                              onChange={(e) => setNotificationData({ ...notificationData, emailBoardUpdates: e.target.checked })}
                              className="h-5 w-5 text-teal-600 focus:ring-teal-500 border-2 border-slate-900 rounded transition-all"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="emailBoardUpdates" className="font-bold text-slate-700">
                              Board Updates
                            </label>
                            <p className="font-medium text-slate-600">Get notified when new boards are added or routes are reset</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="smsReservationReminder"
                              type="checkbox"
                              checked={notificationData.smsReservationReminder}
                              onChange={(e) => setNotificationData({ ...notificationData, smsReservationReminder: e.target.checked })}
                              className="h-5 w-5 text-teal-600 focus:ring-teal-500 border-2 border-slate-900 rounded transition-all"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="smsReservationReminder" className="font-bold text-slate-700">
                              Text Message Reminders
                            </label>
                            <p className="font-medium text-slate-600">
                              Receive SMS reminders 30 minutes before your reservations
                              {!profileData.phone && (
                                <span className="block text-coral-600 font-bold mt-1">
                                  Phone number required - add it in Account settings
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={loading}>
                    {loading ? <LoadingSpinner size="small" /> : 'Save Preferences'}
                  </Button>
                </div>
              </form>
            )}

            {/* Gyms Tab */}
            {activeTab === 'gyms' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <span className="w-1 h-5 bg-teal-500 rounded"></span>
                    My Gyms
                  </h3>
                  <p className="text-sm font-medium text-slate-600 mb-4">
                    Manage your gym memberships. You can quickly switch between gyms using the selector in the header.
                  </p>

                  {/* List of user's gyms */}
                  <div className="space-y-3">
                    {userGyms.map((gym) => (
                      <div
                        key={gym.id}
                        className="flex items-center justify-between p-4 bg-white border-2 border-slate-900 rounded-lg shadow-retro"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-slate-900">{gym.name}</h4>
                            {gym.isPrimary && (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-sunshine-100 text-sunshine-800 border-2 border-sunshine-500">
                                Primary
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-medium text-slate-600">{gym.city}, {gym.state}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {!gym.isPrimary && (
                            <>
                              <Button
                                size="small"
                                variant="outline"
                                onClick={async () => {
                                  setLoading(true);
                                  const result = await setPrimaryGym(gym.id);
                                  if (result.success) {
                                    setMessage({ type: 'success', text: 'Primary gym updated!' });
                                    await updateUser();
                                  } else {
                                    setMessage({ type: 'error', text: result.error });
                                  }
                                  setLoading(false);
                                }}
                                disabled={loading}
                              >
                                Set as Primary
                              </Button>
                              <Button
                                size="small"
                                variant="danger"
                                onClick={async () => {
                                  if (window.confirm(`Remove ${gym.name} from your gyms?`)) {
                                    setLoading(true);
                                    const result = await removeGym(gym.id);
                                    if (result.success) {
                                      setMessage({ type: 'success', text: 'Gym removed!' });
                                    } else {
                                      setMessage({ type: 'error', text: result.error });
                                    }
                                    setLoading(false);
                                  }
                                }}
                                disabled={loading}
                              >
                                Remove
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add new gym section */}
                  <div className="mt-6 p-4 bg-gradient-to-br from-teal-50 to-purple-50 rounded-lg border-2 border-slate-900">
                    <h4 className="font-bold text-slate-900 mb-2">Add Another Gym</h4>
                    <p className="text-sm font-medium text-slate-600 mb-3">
                      Search for and add gyms where you have memberships.
                    </p>

                    <div className="relative">
                      <input
                        type="text"
                        value={gymSearchQuery}
                        onChange={(e) => handleGymSearch(e.target.value)}
                        onFocus={() => setShowGymDropdown(true)}
                        placeholder="Search by gym name, city, or state..."
                        className="block w-full rounded-lg border-2 border-slate-900 px-3 py-2 text-slate-900 font-medium focus:border-teal-500 focus:ring-2 focus:ring-teal-500 transition-all"
                      />

                      {/* Dropdown results */}
                      {showGymDropdown && filteredGyms.length > 0 && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setShowGymDropdown(false)}
                          />
                          <div className="absolute z-20 mt-1 w-full bg-white border-2 border-slate-900 shadow-retro max-h-60 rounded-lg py-1 overflow-auto focus:outline-none">
                            {filteredGyms.filter(g => !userGyms.find(ug => ug.gymId === g.id)).map((gym) => (
                              <button
                                key={gym.id}
                                type="button"
                                onClick={async () => {
                                  setLoading(true);
                                  setShowGymDropdown(false);
                                  const result = await addGym(gym.id);
                                  if (result.success) {
                                    setMessage({ type: 'success', text: `${gym.name} added!` });
                                    setGymSearchQuery('');
                                  } else {
                                    setMessage({ type: 'error', text: result.error });
                                  }
                                  setLoading(false);
                                }}
                                className="w-full text-left px-4 py-2 hover:bg-teal-50 text-slate-900 transition-all touch-manipulation"
                              >
                                <div className="font-bold">{gym.name}</div>
                                <div className="text-sm font-medium text-slate-600">{gym.city}, {gym.state}</div>
                              </button>
                            ))}
                          </div>
                        </>
                      )}

                      {/* No results message */}
                      {showGymDropdown && gymSearchQuery && filteredGyms.filter(g => !userGyms.find(ug => ug.gymId === g.id)).length === 0 && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setShowGymDropdown(false)}
                          />
                          <div className="absolute z-20 mt-1 w-full bg-white border-2 border-slate-900 shadow-retro rounded-lg py-2 px-4 text-sm font-medium text-slate-600">
                            No new gyms found matching "{gymSearchQuery}"
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Booking Usage Tab */}
            {activeTab === 'usage' && bookingUsage && bookingUsage.enabled && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <span className="w-1 h-5 bg-purple-500 rounded"></span>
                    Weekly Booking Usage
                  </h3>
                  <p className="text-sm font-medium text-slate-600 mb-6">
                    Track your reservation time across all gyms to stay within the weekly limit.
                  </p>

                  {loadingUsage ? (
                    <div className="text-center py-8">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Usage Stats Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Current Usage */}
                        <div className="p-6 bg-gradient-to-br from-purple-50 to-magenta-50 border-2 border-slate-900 rounded-lg shadow-retro">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-bold text-slate-600">Current Usage</span>
                            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="text-3xl font-bold text-slate-900">{bookingUsage.currentHours}h</div>
                          <div className="text-xs font-medium text-slate-600 mt-1">
                            {bookingUsage.currentMinutes} minutes
                          </div>
                        </div>

                        {/* Weekly Limit */}
                        <div className="p-6 bg-gradient-to-br from-teal-50 to-cyan-50 border-2 border-slate-900 rounded-lg shadow-retro">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-bold text-slate-600">Weekly Limit</span>
                            <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="text-3xl font-bold text-slate-900">{bookingUsage.limitHours}h</div>
                          <div className="text-xs font-medium text-slate-600 mt-1">
                            {bookingUsage.limitMinutes} minutes
                          </div>
                        </div>

                        {/* Remaining */}
                        <div className={`p-6 border-2 border-slate-900 rounded-lg shadow-retro ${
                          parseFloat(bookingUsage.utilizationPercentage) >= 90
                            ? 'bg-gradient-to-br from-coral-50 to-red-50'
                            : 'bg-gradient-to-br from-sunshine-50 to-yellow-50'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-bold text-slate-600">Remaining</span>
                            <svg className="w-5 h-5 text-sunshine-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                          </div>
                          <div className="text-3xl font-bold text-slate-900">{bookingUsage.remainingHours}h</div>
                          <div className="text-xs font-medium text-slate-600 mt-1">
                            {bookingUsage.remainingMinutes} minutes
                          </div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="p-6 bg-white border-2 border-slate-900 rounded-lg shadow-retro">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-bold text-slate-900">Utilization</h4>
                          <span className={`text-2xl font-bold ${
                            parseFloat(bookingUsage.utilizationPercentage) >= 90
                              ? 'text-coral-600'
                              : parseFloat(bookingUsage.utilizationPercentage) >= 75
                              ? 'text-sunshine-600'
                              : 'text-teal-600'
                          }`}>
                            {bookingUsage.utilizationPercentage}%
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-6 border-2 border-slate-900 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              parseFloat(bookingUsage.utilizationPercentage) >= 90
                                ? 'bg-gradient-to-r from-coral-500 to-red-500'
                                : parseFloat(bookingUsage.utilizationPercentage) >= 75
                                ? 'bg-gradient-to-r from-sunshine-500 to-yellow-500'
                                : 'bg-gradient-to-r from-teal-500 to-cyan-500'
                            }`}
                            style={{ width: `${Math.min(parseFloat(bookingUsage.utilizationPercentage), 100)}%` }}
                          />
                        </div>
                        <p className="text-xs font-medium text-slate-600 mt-2">
                          You've used {bookingUsage.utilizationPercentage}% of your weekly booking allowance
                        </p>
                      </div>

                      {/* Info Box */}
                      <div className={`p-6 border-2 rounded-lg ${
                        parseFloat(bookingUsage.utilizationPercentage) >= 90
                          ? 'bg-coral-50 border-coral-500'
                          : 'bg-blue-50 border-blue-500'
                      }`}>
                        <div className="flex items-start gap-3">
                          <svg className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                            parseFloat(bookingUsage.utilizationPercentage) >= 90 ? 'text-coral-600' : 'text-blue-600'
                          }`} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <h4 className={`text-sm font-bold mb-2 ${
                              parseFloat(bookingUsage.utilizationPercentage) >= 90 ? 'text-coral-900' : 'text-blue-900'
                            }`}>
                              {parseFloat(bookingUsage.utilizationPercentage) >= 90
                                ? 'Approaching Limit!'
                                : 'About Your Booking Limit'}
                            </h4>
                            <p className={`text-sm font-medium ${
                              parseFloat(bookingUsage.utilizationPercentage) >= 90 ? 'text-coral-800' : 'text-blue-800'
                            }`}>
                              {parseFloat(bookingUsage.utilizationPercentage) >= 90 ? (
                                <>
                                  You're close to your weekly limit! You have <strong>{bookingUsage.remainingHours} hours</strong> remaining.
                                  Consider canceling unused reservations to free up time.
                                </>
                              ) : (
                                <>
                                  Your usage is calculated based on active reservations in a rolling {bookingUsage.windowDays}-day window
                                  across all gyms. You currently have <strong>{bookingUsage.reservationCount}</strong> active {bookingUsage.reservationCount === 1 ? 'reservation' : 'reservations'}.
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Refresh Button */}
                      <div className="flex justify-end">
                        <Button
                          onClick={loadBookingUsage}
                          variant="outline"
                          disabled={loadingUsage}
                        >
                          {loadingUsage ? 'Refreshing...' : 'Refresh Usage'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
